import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, CreditCard, Users, AlertTriangle, ShieldOff, Globe2,
  TrendingUp, RefreshCcw, Receipt, Mail,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { API } from "@/App";
import { cn } from "@/lib/utils";

function Stat({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-4">
      <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2">
        <Icon className={cn("h-3 w-3", accent || "text-emerald-400")} />
        {label}
      </div>
      <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
      {sub && <div className="text-[0.65rem] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function BillingTab({ registerRefresh }) {
  const [data, setData] = useState(null);
  const [dunning, setDunning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [nudgingId, setNudgingId] = useState(null);

  const sendNudge = async (userId) => {
    setNudgingId(userId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers = { Authorization: `Bearer ${session?.access_token || ""}` };
      const res = await axios.post(
        `${API}/admin/pulse/dunning/send-nudge`,
        { user_id: userId },
        { headers }
      );
      toast.success(`Dunning email sent to ${res.data.to} · ${res.data.bucket}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send nudge email");
    } finally {
      setNudgingId(null);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers = { Authorization: `Bearer ${session?.access_token || ""}` };
      const [overview, dq] = await Promise.allSettled([
        axios.get(`${API}/admin/metrics/billing`, { headers }),
        axios.get(`${API}/admin/pulse/billing-dunning`, { headers }),
      ]);
      if (overview.status === "fulfilled") setData(overview.value.data);
      if (dq.status === "fulfilled") setDunning(dq.value.data);
      if (overview.status === "rejected") {
        toast.error(overview.reason?.response?.data?.detail || "Failed to load billing overview");
      }
      if (dq.status === "rejected") {
        toast.error(dq.reason?.response?.data?.detail || "Failed to load dunning queue");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (registerRefresh) registerRefresh(() => fetchData);
  }, [registerRefresh, fetchData]);

  const filteredRows = useMemo(() => {
    const rows = dunning?.failed_rows || [];
    if (filter === "all") return rows;
    return rows.filter((r) => r.failure_bucket === filter);
  }, [dunning, filter]);

  const bucketCounts = useMemo(() => {
    const m = { all: 0 };
    (dunning?.failed_rows || []).forEach((r) => {
      m.all += 1;
      m[r.failure_bucket] = (m[r.failure_bucket] || 0) + 1;
    });
    return m;
  }, [dunning]);

  if (loading) {
    return (
      <div className="py-20 text-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin inline" />
      </div>
    );
  }

  const total = data?.total_chefs || 0;
  const proPct = total > 0 ? ((data.active_pro / total) * 100).toFixed(1) : "0.0";
  const mrr = dunning?.expected_mrr_next_month_usd ?? 0;
  const activeSubs = dunning?.active_subscriptions ?? 0;

  return (
    <div className="space-y-6" data-testid="hq-tab-billing">
      {/* ======= Top-line forecast strip ======= */}
      <div
        className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-slate-950 to-slate-900 p-5 relative overflow-hidden"
        data-testid="billing-mrr-forecast"
      >
        <div className="absolute inset-y-0 right-0 w-1/3 opacity-20 pointer-events-none">
          <TrendingUp className="h-full w-full text-emerald-400" />
        </div>
        <div className="relative">
          <div className="text-[0.65rem] uppercase tracking-[0.2em] text-emerald-400 font-mono mb-2 flex items-center gap-2">
            <TrendingUp className="h-3 w-3" /> Expected Next-Month MRR
          </div>
          <div className="text-4xl font-bold text-white tabular-nums" data-testid="billing-mrr-value">
            {mrr.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 })}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Based on <span className="text-emerald-300 font-semibold">{activeSubs}</span> active subscriptions
            renewing in the next 30 days. INR charges are USD-equivalent (~₹83/USD).
          </div>
        </div>
      </div>

      {/* ======= Portfolio snapshot ======= */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={CreditCard} label="Active Pro" value={data.active_pro} sub={`${proPct}% conversion`} />
          <Stat icon={Users} label="Free Tier" value={data.free_tier} accent="text-slate-400" />
          <Stat icon={AlertTriangle} label="Expiring ≤ 30d" value={data.expiring_within_30d} accent="text-amber-400" />
          <Stat icon={ShieldOff} label="Suspended" value={data.suspended} accent="text-rose-400" />
        </div>
      )}

      {/* ======= Dunning & Receivables ======= */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden" data-testid="billing-dunning-table">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-rose-500/10 flex items-center justify-center">
              <Receipt className="h-4 w-4 text-rose-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Dunning &amp; Receivables</h3>
              <p className="text-xs text-slate-500">
                Outstanding + failed subscriptions. Grouped by failure reason.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={fetchData}
            className="text-xs text-slate-400 hover:text-amber-300 flex items-center gap-1"
            data-testid="billing-dunning-refresh"
          >
            <RefreshCcw className="h-3 w-3" /> Refresh
          </button>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 px-5 py-3 bg-slate-950/40 border-b border-slate-800">
          {["all", "Expired Card", "Deactivated / Stolen", "Insufficient Funds", "Issuer Declined", "Auth / OTP Failed", "User Abandoned", "Network / Gateway", "Other"].map((b) => {
            const c = bucketCounts[b] || 0;
            if (b !== "all" && c === 0) return null;
            const active = filter === b;
            return (
              <button
                key={b}
                type="button"
                onClick={() => setFilter(b)}
                className={cn(
                  "text-[11px] font-medium rounded-full px-3 py-1 transition-colors",
                  active
                    ? "bg-amber-500 text-slate-950"
                    : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                )}
                data-testid={`billing-dunning-filter-${b.replace(/\s|\//g, "-").toLowerCase()}`}
              >
                {b === "all" ? "All" : b}
                <span className={cn(
                  "ml-1.5 text-[10px] tabular-nums",
                  active ? "text-slate-900" : "text-slate-500",
                )}>
                  {c}
                </span>
              </button>
            );
          })}
        </div>

        {filteredRows.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500 italic">
            No failed payments{filter !== "all" ? ` in "${filter}"` : ""}. 🎉
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-[0.65rem] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Customer</th>
                  <th className="text-left px-4 py-2.5 font-medium">Reason</th>
                  <th className="text-right px-4 py-2.5 font-medium">Amount</th>
                  <th className="text-center px-4 py-2.5 font-medium">Retries</th>
                  <th className="text-right px-4 py-2.5 font-medium">Last Attempt</th>
                  <th className="text-right px-4 py-2.5 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr
                    key={`dunning-${r.user_id}`}
                    className="border-t border-slate-800 hover:bg-amber-500/5"
                    data-testid={`billing-dunning-row-${r.user_id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{r.display}</div>
                      <div className="text-[0.65rem] text-slate-500 font-mono mt-0.5">
                        {r.gateway?.toUpperCase()} · {r.tier || "individual"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-rose-500/10 text-rose-300 border border-rose-500/30">
                        {r.failure_bucket}
                      </span>
                      {r.raw_reason && (
                        <div className="text-[0.65rem] text-slate-500 mt-1 font-mono truncate max-w-[220px]">
                          {r.raw_reason}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-amber-400 tabular-nums">
                      {r.amount_display}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={cn(
                        "inline-block tabular-nums font-bold rounded px-2 py-0.5 text-xs",
                        r.retry_count >= 10 ? "bg-rose-500/15 text-rose-300" :
                        r.retry_count >= 3  ? "bg-amber-500/15 text-amber-300" :
                                               "bg-slate-800 text-slate-300"
                      )}>
                        {r.retry_count}×
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-400 font-mono text-xs">
                      {(r.last_attempt_at || "").slice(0, 10)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => sendNudge(r.user_id)}
                        disabled={nudgingId === r.user_id}
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-semibold transition-colors",
                          nudgingId === r.user_id
                            ? "bg-slate-700 text-slate-400 cursor-wait"
                            : "bg-amber-500/15 text-amber-300 hover:bg-amber-500 hover:text-slate-950 border border-amber-500/30"
                        )}
                        data-testid={`billing-dunning-nudge-${r.user_id}`}
                      >
                        {nudgingId === r.user_id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Mail className="h-3 w-3" />}
                        {nudgingId === r.user_id ? "Sending…" : "Email nudge"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ======= Region split (legacy card) ======= */}
      {data?.region_split && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-5">
          <h3 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono mb-3 flex items-center gap-2">
            <Globe2 className="h-3 w-3 text-emerald-400" />
            Billing Region Split
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <Row label="🌍 Rest of World (xPay)" value={data.region_split.RoW} />
            <Row label="🇮🇳 India (Razorpay)" value={data.region_split.IN} />
            <Row label="Unknown" value={data.region_split.Unknown} muted />
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, highlight, muted }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-slate-950/40 border border-slate-800 px-3 py-2">
      <span className={cn("text-slate-400", muted && "text-slate-600 text-xs")}>{label}</span>
      <span className={cn(
        "tabular-nums font-semibold",
        highlight ? "text-emerald-300" : muted ? "text-slate-500" : "text-white"
      )}>
        {value}
      </span>
    </div>
  );
}
